import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CRow,
  CAlert,
  CSpinner,
  CBadge,
  CListGroup,
  CListGroupItem,
} from '@coreui/react'
import {
  cilPhone,
  cilXCircle,
  cilList,
  cilArrowCircleLeft,
  cilVerticalAlignCenter,
  cilEnvelopeClosed,
  cilPlus,
  cilFactory,
} from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { buildApiUrl } from '../../utils/getConfiguration'
import PartnerInfoRow from '../../components/PartnerInfoRow'
import {
  sanitizeValue,
  handlePhoneClick,
  handleMailClick,
  readSupplierAssignment,
  writeSupplierAssignment,
  clearSupplierAssignment,
  filterManualSuppliers,
  filterPreferredSuppliers,
} from '../../utils/supplierHelpers'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'
import { SplashScreen } from '@capacitor/splash-screen'

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState({ list: [], preferred: [], manualPreferred: [] })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [visible, setVisible] = useState(false)
  const [selectedSupplierId, setSelectedSupplierId] = useState(null)
  const [isMutating, setIsMutating] = useState(false)
  const { t } = useTranslation()

  const accountContext = useMemo(() => {
    if (typeof window === 'undefined') return null
    const account_ID = window.localStorage.getItem('account_ID')
    const reminder_ID = window.localStorage.getItem('taskid')
    const grp_ID = window.localStorage.getItem('grp_ID')
    return { account_ID, reminder_ID, grp_ID }
  }, [])

  const refreshVisibility = useCallback(() => {
    const { supplierName, supplierPhone } = readSupplierAssignment()
    setVisible(!(sanitizeValue(supplierName) || sanitizeValue(supplierPhone)))
  }, [])

  const fetchSuppliers = useCallback(
    async (signal) => {
      if (!accountContext?.grp_ID) return
      setLoading(true)
      setError(null)
      try {
        const { account_ID, reminder_ID, grp_ID } = accountContext
        const paramsBase = { grp_ID, account_ID, reminder_ID }
        const requestConfig = signal ? { signal } : {}

        const [allRes, preferredRes, manualRes] = await Promise.all([
          apiClient.get(buildApiUrl('suppliers', { grp_ID }), requestConfig),
          apiClient.get(buildApiUrl('supplierPref', paramsBase), requestConfig),
          apiClient.get(buildApiUrl('supplierPrefManual', paramsBase), requestConfig),
        ])

        setSuppliers({
          list: Array.isArray(allRes.data) ? allRes.data : [],
          preferred: Array.isArray(preferredRes.data) ? preferredRes.data : [],
          manualPreferred: Array.isArray(manualRes.data) ? manualRes.data : [],
        })
        refreshVisibility()
      } catch (fetchError) {
        if (apiClient.isCancel?.(fetchError) || fetchError?.name === 'CanceledError') return
        console.error('Error fetching suppliers', fetchError)
        setError(t('suppliers.errors.load'))
      } finally {
        setLoading(false)
      }
    },
    [accountContext, refreshVisibility, t],
  )

  useEffect(() => {
    if (!accountContext?.grp_ID) return undefined
    const controller = new AbortController()
    fetchSuppliers(controller.signal)
    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    if (window?.SplashScreen?.hide) {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) =>
        console.warn('Unable to hide splash screen', error),
      )
    }
    return () => controller.abort()
  }, [accountContext, fetchSuppliers])

  let navigate = useNavigate()

  const handleClickUnassign = useCallback(async () => {
    if (isMutating) {
      return
    }
    try {
      setIsMutating(true)
      const { account_ID, reminder_ID } = accountContext || {}
      if (!account_ID || !reminder_ID) return
      await apiClient.get(buildApiUrl('supplierUnassign', { account_ID, reminder_ID }))
      setSelectedSupplierId(null)
      clearSupplierAssignment()
      await fetchSuppliers()
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsMutating(false)
    }
  }, [accountContext, fetchSuppliers, isMutating])

  const handleClickUnassignMan = useCallback(async () => {
    if (isMutating) {
      return
    }
    try {
      setIsMutating(true)
      const { account_ID, reminder_ID } = accountContext || {}
      if (!account_ID || !reminder_ID) return
      await apiClient.get(buildApiUrl('supplierUnassignManual', { account_ID, reminder_ID }))
      clearSupplierAssignment()
      setSelectedSupplierId(null)
      await fetchSuppliers()
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsMutating(false)
    }
  }, [accountContext, fetchSuppliers, isMutating])

  const handleClickAssign = useCallback(
    async (supplierId) => {
      if (isMutating) {
        return
      }
      try {
        setIsMutating(true)
        const { account_ID, reminder_ID } = accountContext || {}
        const nextSupplierId = supplierId ?? selectedSupplierId
        if (!account_ID || !reminder_ID || !nextSupplierId) return
        await apiClient.get(
          buildApiUrl('supplierAssign', {
            account_ID,
            reminder_ID,
            supplier_ID: nextSupplierId,
          }),
        )
        setSelectedSupplierId(null)
        const supplierMatch =
          suppliers.list.find((item) => String(item.supplier_id) === String(nextSupplierId)) ||
          suppliers.preferred.find((item) => String(item.supplier_id) === String(nextSupplierId)) ||
          suppliers.manualPreferred.find(
            (item) => String(item.supplier_id) === String(nextSupplierId),
          )

        writeSupplierAssignment({
          supplierId: String(nextSupplierId),
          supplierName:
            sanitizeValue(
              supplierMatch?.supplierName ??
                supplierMatch?.supplier_name ??
                supplierMatch?.name ??
                `Supplier #${nextSupplierId}`,
            ) || `Supplier #${nextSupplierId}`,
          supplierPhone: sanitizeValue(
            supplierMatch?.supplierPhone ??
              supplierMatch?.supplier_contact_tel ??
              supplierMatch?.phone,
          ),
        })
        await fetchSuppliers()
        refreshVisibility()
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setIsMutating(false)
      }
    },
    [accountContext, fetchSuppliers, isMutating, refreshVisibility, selectedSupplierId, suppliers],
  )

  const manualSuppliers = useMemo(
    () => filterManualSuppliers(suppliers.manualPreferred),
    [suppliers.manualPreferred],
  )

  const preferredSuppliers = useMemo(
    () => filterPreferredSuppliers(suppliers.preferred),
    [suppliers.preferred],
  )

  const handleNavigateBack = useCallback(() => {
    console.log('Back')
    window.history.back()
  }, [])

  return (
    <div className="suppliers-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader title={t('suppliers.header.title')} onBack={handleNavigateBack} />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            <CCard className="bg-light border-0 shadow-sm">
              <CCardBody>
                <CRow className="align-items-center mb-3 g-3">
                  <CCol xs="auto">
                    <h6 className="info-navbar__brand-title fw-semibold mb-0">
                      {t('suppliers.sections.preferredTitle')}
                    </h6>
                  </CCol>
                  <CCol xs="auto" className="d-flex gap-2 align-items-center flex-wrap">
                    <CBadge color={manualSuppliers.length === 0 ? 'warning' : 'success'}>
                      {t('suppliers.badges.manual', { count: manualSuppliers.length })}
                    </CBadge>
                    <CBadge color={preferredSuppliers.length === 0 ? 'warning' : 'info'}>
                      {t('suppliers.badges.automatic', { count: preferredSuppliers.length })}
                    </CBadge>
                  </CCol>
                  <CCol xs="auto">
                    <CButton
                      color="primary"
                      size="sm"
                      aria-label={t('suppliers.actions.setPreferred')}
                      onClick={() => navigate('/suppliersselection')}
                    >
                      <CIcon icon={cilPlus} />
                    </CButton>
                  </CCol>
                </CRow>

                {visible &&
                  !loading &&
                  manualSuppliers.length === 0 &&
                  preferredSuppliers.length === 0 && (
                    <div className="reminder-area-card__item mb-4">
                      <div className="reminder-area-card__empty">
                        <span
                          className="reminder-area-card__empty-icon text-primary"
                          aria-hidden="true"
                        >
                          <CIcon icon={cilFactory} size="lg" />
                        </span>
                        <div>
                          <span className="reminder-area-card__empty-title">
                            {t('suppliers.empty.preferredTitle')}
                          </span>
                          <span className="reminder-area-card__empty-caption">
                            {t('suppliers.empty.preferredMessage')}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                {manualSuppliers.length > 0 && (
                  <div className="mt-4">
                    <h5 className="card-title">{t('suppliers.sections.manualTitle')}</h5>
                    <CListGroup className="list-group-flush d-grid gap-3">
                      {manualSuppliers.map((manual) => {
                        const name =
                          sanitizeValue(manual.supplier_name) || t('suppliers.fallbacks.manual')
                        const contactName = sanitizeValue(manual.supplier_contact_name)
                        const contactTel = sanitizeValue(manual.supplier_contact_tel)
                        const contactMail = sanitizeValue(manual.supplier_contact_mail)
                        const picture = manual.pic
                          ? manual.pic
                          : manual.supplierPic
                            ? `https://displatform.com/assets/suppliers/${manual.supplierPic}`
                            : null
                        const infoLines = [
                          contactName,
                          contactTel ? t('suppliers.labels.phone', { value: contactTel }) : null,
                          contactMail ? t('suppliers.labels.email', { value: contactMail }) : null,
                        ].filter(Boolean)
                        return (
                          <CListGroupItem
                            key={manual.supplier_id}
                            className="d-flex align-items-center gap-3 flex-wrap p-3 border-0 shadow-sm rounded-3"
                          >
                            <PartnerInfoRow
                              icon={
                                <CIcon
                                  icon={cilFactory}
                                  size="lg"
                                  className="text-primary flex-shrink-0"
                                  aria-hidden="true"
                                />
                              }
                              picture={picture}
                              title={name}
                              lines={infoLines}
                              imageWidth={56}
                            />
                            <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                              <CButton
                                color={contactTel ? 'primary' : 'secondary'}
                                disabled={!contactTel}
                                onClick={() => handlePhoneClick(contactTel)}
                                aria-label={t('suppliers.actions.call', { name })}
                              >
                                <CIcon icon={cilPhone} />
                              </CButton>
                              <CButton
                                color={contactMail ? 'primary' : 'secondary'}
                                disabled={!contactMail}
                                onClick={() => handleMailClick(contactMail, name)}
                                aria-label={t('suppliers.actions.email', { name })}
                              >
                                <CIcon icon={cilEnvelopeClosed} />
                              </CButton>
                              <CButton
                                color="danger"
                                onClick={handleClickUnassignMan}
                                aria-label={t('suppliers.actions.unassign', { name })}
                                disabled={isMutating}
                              >
                                <CIcon icon={cilXCircle} />
                              </CButton>
                            </div>
                          </CListGroupItem>
                        )
                      })}
                    </CListGroup>
                  </div>
                )}

                {preferredSuppliers.length > 0 && (
                  <div className="mt-4">
                    <h5 className="card-title">{t('suppliers.sections.listedTitle')}</h5>
                    <CListGroup className="list-group-flush d-grid gap-3">
                      {preferredSuppliers.map((prefsupplier) => {
                        const name =
                          sanitizeValue(prefsupplier.supplierName) ||
                          t('suppliers.fallbacks.preferred')
                        const rawDescription = sanitizeValue(prefsupplier.description)
                        const descriptionText =
                          rawDescription.length > 100
                            ? `${rawDescription.substring(0, 100)}...`
                            : rawDescription
                        const phone = sanitizeValue(prefsupplier.supplierPhone)
                        const mail = sanitizeValue(prefsupplier.supplierMail)
                        const picture = prefsupplier.supplierPic
                          ? `https://displatform.com/assets/suppliers/${prefsupplier.supplierPic}`
                          : prefsupplier.pic
                            ? prefsupplier.pic
                            : null
                        const infoLines = [
                          descriptionText,
                          phone ? t('suppliers.labels.phone', { value: phone }) : null,
                          mail ? t('suppliers.labels.email', { value: mail }) : null,
                        ].filter(Boolean)
                        return (
                          <CListGroupItem
                            key={prefsupplier.supplier_id}
                            className="d-flex align-items-center gap-3 flex-wrap p-3 border-0 shadow-sm rounded-3"
                          >
                            <PartnerInfoRow
                              icon={
                                <CIcon
                                  icon={cilFactory}
                                  size="lg"
                                  className="text-primary flex-shrink-0"
                                  aria-hidden="true"
                                />
                              }
                              picture={picture}
                              title={name}
                              lines={infoLines}
                              imageWidth={56}
                            />
                            <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                              <CButton
                                color={phone ? 'primary' : 'secondary'}
                                disabled={!phone}
                                onClick={() => handlePhoneClick(phone)}
                                aria-label={t('suppliers.actions.call', { name })}
                              >
                                <CIcon icon={cilPhone} />
                              </CButton>
                              <CButton
                                color={mail ? 'primary' : 'secondary'}
                                disabled={!mail}
                                onClick={() => handleMailClick(mail, prefsupplier.supplierName)}
                                aria-label={t('suppliers.actions.email', { name })}
                              >
                                <CIcon icon={cilEnvelopeClosed} />
                              </CButton>
                              <CButton
                                color="danger"
                                onClick={handleClickUnassign}
                                aria-label={t('suppliers.actions.unassign', { name })}
                                disabled={isMutating}
                              >
                                <CIcon icon={cilXCircle} />
                              </CButton>
                            </div>
                          </CListGroupItem>
                        )
                      })}
                    </CListGroup>
                  </div>
                )}
              </CCardBody>
            </CCard>
          </CCol>

          <CCol lg={10}>
            <CCard className="border-0 shadow-sm">
              <CCardBody>
                <CRow className="align-items-center mb-3 g-3">
                  <CCol xs="auto">
                    <h6 className="info-navbar__brand-title fw-semibold mb-0">
                      {t('suppliers.sections.selectTitle')}
                    </h6>
                  </CCol>
                  <CCol xs="auto">
                    <CBadge color={suppliers.list.length === 0 ? 'warning' : 'primary'}>
                      {suppliers.list.length}
                    </CBadge>
                  </CCol>
                </CRow>
                {loading && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <CSpinner size="sm" /> {t('suppliers.loading.suppliers')}
                  </div>
                )}
                {error && <CAlert color="danger">{error}</CAlert>}
                {!loading && !error && suppliers.list.length === 0 && (
                  <div className="reminder-area-card__item">
                    <div className="reminder-area-card__empty">
                      <span
                        className="reminder-area-card__empty-icon text-primary"
                        aria-hidden="true"
                      >
                        <CIcon icon={cilFactory} size="lg" />
                      </span>
                      <div>
                        <span className="reminder-area-card__empty-title">
                          {t('suppliers.empty.listTitle')}
                        </span>
                        <span className="reminder-area-card__empty-caption">
                          {t('suppliers.empty.listMessage')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {suppliers.list.length > 0 && (
                  <CListGroup className="list-group-flush d-grid gap-3">
                    {suppliers.list.map((supplier) => {
                      const name =
                        sanitizeValue(supplier.supplierName) || t('suppliers.fallbacks.general')
                      const rawDescription = sanitizeValue(supplier.description)
                      const description =
                        rawDescription.length > 100
                          ? `${rawDescription.substring(0, 100)}...`
                          : rawDescription
                      const phone = sanitizeValue(supplier.supplierPhone)
                      const mail = sanitizeValue(supplier.supplierMail)
                      const picture = supplier.pic
                        ? supplier.pic
                        : supplier.supplierPic
                          ? `https://displatform.com/assets/suppliers/${supplier.supplierPic}`
                          : null
                      const infoLines = [
                        description,
                        phone ? t('suppliers.labels.phone', { value: phone }) : null,
                        mail ? t('suppliers.labels.email', { value: mail }) : null,
                      ].filter(Boolean)
                      return (
                        <CListGroupItem
                          key={supplier.supplier_id}
                          className="d-flex align-items-center gap-3 flex-wrap p-3 border-0 shadow-sm rounded-3"
                        >
                          <PartnerInfoRow
                            icon={
                              <CIcon
                                icon={cilFactory}
                                size="lg"
                                className="text-primary flex-shrink-0"
                                aria-hidden="true"
                              />
                            }
                            picture={picture}
                            title={name}
                            lines={infoLines}
                            imageWidth={56}
                          />
                          <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                            <CButton
                              color={phone ? 'primary' : 'secondary'}
                              disabled={!phone}
                              onClick={() => handlePhoneClick(phone)}
                              aria-label={t('suppliers.actions.call', { name })}
                            >
                              <CIcon icon={cilPhone} />
                            </CButton>
                            <CButton
                              color={mail ? 'primary' : 'secondary'}
                              disabled={!mail}
                              onClick={() => handleMailClick(mail, name)}
                              aria-label={t('suppliers.actions.email', { name })}
                            >
                              <CIcon icon={cilEnvelopeClosed} />
                            </CButton>
                            <CButton
                              color="primary"
                              onClick={() => {
                                const supplierIdString = String(supplier.supplier_id ?? '')
                                writeSupplierAssignment({
                                  supplierId: supplierIdString,
                                  supplierName: name,
                                  supplierPhone: phone,
                                })
                                setSelectedSupplierId(supplier.supplier_id)
                                navigate('/supplierdetail')
                              }}
                              aria-label={t('suppliers.actions.detail', { name })}
                              disabled={isMutating}
                            >
                              <CIcon icon={cilList} />
                            </CButton>
                            <CButton
                              color="success"
                              onClick={() => {
                                setSelectedSupplierId(supplier.supplier_id)
                                handleClickAssign(supplier.supplier_id)
                              }}
                              aria-label={t('suppliers.actions.assign', { name })}
                              disabled={isMutating}
                            >
                              <CIcon icon={cilVerticalAlignCenter} />
                            </CButton>
                          </div>
                        </CListGroupItem>
                      )
                    })}
                  </CListGroup>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Suppliers
